import React, { useEffect, useState } from 'react';
import { 
  useParams, 
  useLocation, 
  useNavigate, 
  useRoutes,
  useSearchParams,
  useMatch,
  Routes,
  Route
} from 'react-router-dom';

// Test component for basic route matching
const BasicTest: React.FC = () => {
  const params = useParams<{ id: string }>();
  return (
    <div style={{ padding: '20px', background: '#e0f0e0' }}>
      <h3>Basic Test Component</h3>
      <p>ID from params: {params.id || 'undefined'}</p>
      <p>Params object: {JSON.stringify(params)}</p>
    </div>
  );
};

// Test component for nested route matching
const NestedTest: React.FC = () => {
  const params = useParams<{ projectId: string; feature: string }>();
  return (
    <div style={{ padding: '20px', background: '#f0e0e0' }}>
      <h3>Nested Test Component</h3>
      <p>Project ID: {params.projectId || 'undefined'}</p>
      <p>Feature: {params.feature || 'undefined'}</p>
      <p>Full params: {JSON.stringify(params)}</p>
    </div>
  );
};

export const RouterDebug: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [renderCount, setRenderCount] = useState(0);

  // Test various route patterns
  const basicMatch = useMatch('/debug/basic/:id');
  const nestedMatch = useMatch('/debug/nested/:projectId/:feature');

  useEffect(() => {
    setRenderCount(prev => prev + 1);
    
    // Comprehensive debug information
    const info = {
      timestamp: new Date().toISOString(),
      renderCount: renderCount + 1,
      
      // React Router hooks
      hooks: {
        useParams: {
          value: params,
          type: typeof params,
          keys: Object.keys(params || {}),
          isEmpty: Object.keys(params || {}).length === 0,
          isNull: params === null,
          isUndefined: params === undefined
        },
        useLocation: {
          pathname: location?.pathname,
          search: location?.search,
          hash: location?.hash,
          state: location?.state,
          key: location?.key
        },
        useNavigate: {
          available: typeof navigate === 'function',
          type: typeof navigate
        },
        useSearchParams: {
          entries: Array.from(searchParams.entries())
        },
        useMatch: {
          basic: basicMatch,
          nested: nestedMatch
        }
      },
      
      // Window location
      window: {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      },
      
      // React version and mode
      react: {
        version: React.version,
        strictMode: document.querySelector('body')?.getAttribute('data-strict-mode') || 'unknown'
      },
      
      // Component tree depth (approximate)
      componentDepth: (() => {
        let depth = 0;
        let node = document.getElementById('router-debug');
        while (node && depth < 50) {
          node = node.parentElement;
          depth++;
        }
        return depth;
      })()
    };
    
    setDebugInfo(info);
    console.log('🔍 [RouterDebug] Comprehensive analysis:', info);
  }, [params, location, navigate, searchParams, basicMatch, nestedMatch, renderCount]);

  return (
    <div id="router-debug" style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>React Router Debug Panel</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Test Navigation</h3>
        <button onClick={() => navigate('/debug/basic/123')}>
          Navigate to Basic Route (/debug/basic/123)
        </button>
        <button onClick={() => navigate('/debug/nested/456/scenarios')} style={{ marginLeft: '10px' }}>
          Navigate to Nested Route (/debug/nested/456/scenarios)
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Current Debug Info</h3>
        <pre style={{ background: '#f0f0f0', padding: '10px', overflow: 'auto' }}>
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Inline Route Tests</h3>
        <Routes>
          <Route path="basic/:id" element={<BasicTest />} />
          <Route path="nested/:projectId/:feature" element={<NestedTest />} />
          <Route path="*" element={
            <div style={{ padding: '10px', background: '#ffe0e0' }}>
              No route matched. Current path: {location.pathname}
            </div>
          } />
        </Routes>
      </div>
    </div>
  );
};